import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, ExternalLink, Eye, MessageCircle, Phone, Loader2, Crown,
  CircleDot, PencilLine, Sparkles, Inbox,
} from "lucide-react";
import { api, siteHtmlUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";

const STATUS = {
  live: { label: "Live", cls: "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30" },
  draft: { label: "Draft", cls: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sites, setSites] = useState(null);
  const [unreadBySite, setUnreadBySite] = useState({});

  useEffect(() => {
    api.get("/sites").then(({ data }) => setSites(data.sites)).catch(() => setSites([]));
    api.get("/leads/summary").then(({ data }) => setUnreadBySite(data.unread_by_site || {})).catch(() => {});
  }, []);

  const plan = user?.plan || {};
  const active = plan.status === "active";
  const slotsUsed = (plan.sites_covered || []).length;
  const slotsTotal = plan.slots || 0;

  return (
    <div className="min-h-screen">
      <TopBar active="dashboard" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">Your websites</h1>
            <p className="text-zinc-400 text-sm mt-1">Manage, preview and publish your business sites.</p>
          </div>
          <button data-testid="dashboard-create-site-btn" onClick={() => navigate("/create")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] active:scale-[0.98] transition-all">
            <Plus className="w-4 h-4" /> Create New Site
          </button>
        </div>

        {/* Subscription widget */}
        <div data-testid="dashboard-subscription-status"
          className={`mb-8 rounded-2xl border p-5 flex flex-wrap items-center justify-between gap-4 ${active ? "border-[#C9A24B]/30 bg-[#C9A24B]/5" : "border-[#27272A] bg-[#161616]"}`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${active ? "bg-gradient-to-br from-[#C9A24B] to-[#E5C378] text-[#0A0A0A]" : "bg-[#1C1C1E] text-[#C9A24B]"}`}>
              <Crown className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display text-lg font-bold">
                {active ? `${(plan.plan_id || "").replace(/_.*/, "").toUpperCase()} Plan · Active` : "No active plan"}
              </div>
              <div className="text-sm text-zinc-400">
                {active
                  ? `${slotsUsed}/${slotsTotal} website slot${slotsTotal > 1 ? "s" : ""} used · Renews ${plan.expires_at}`
                  : "Pick a plan to publish your websites live"}
              </div>
            </div>
          </div>
          <button data-testid="dashboard-manage-sub-btn" onClick={() => navigate("/subscribe")}
            className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${active ? "bg-[#1C1C1E] border border-[#27272A] text-zinc-200" : "bg-[#25D366] text-[#04120a]"}`}>
            {active ? "Manage" : "See Plans"}
          </button>
        </div>

        {/* Sites grid */}
        {sites === null ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A24B] wt-spin" /></div>
        ) : sites.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#27272A] py-20 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#C9A24B]/10 flex items-center justify-center mb-5">
              <Sparkles className="w-8 h-8 text-[#C9A24B]" />
            </div>
            <h3 className="font-display text-xl font-bold">No websites yet</h3>
            <p className="text-zinc-400 text-sm mt-2 mb-6">Create your first AI-generated website in under a minute.</p>
            <button onClick={() => navigate("/create")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A]">
              <Plus className="w-4 h-4" /> Create Website
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sites.map((s, i) => {
              const st = STATUS[s.status] || STATUS.draft;
              const unread = unreadBySite[s.id] || 0;
              return (
                <div key={s.id} data-testid={`dashboard-site-card-${i}`}
                  className="bg-[#161616] border border-[#27272A] rounded-2xl overflow-hidden hover:border-[#C9A24B]/30 transition-all group">
                  <div className="relative h-36 bg-[#0A0A0A] overflow-hidden">
                    <iframe title={s.id} src={siteHtmlUrl(s.id)} tabIndex={-1}
                      className="pointer-events-none origin-top-left"
                      style={{ width: 1100, height: 700, transform: "scale(0.34)", border: "none" }} />
                    <div className="absolute top-3 left-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${st.cls}`}>
                        <CircleDot className="w-3 h-3" /> {st.label}
                      </span>
                    </div>
                    {unread > 0 && (
                      <button data-testid={`dashboard-leads-summary-card-${i}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/leads?site=${s.id}&filter=unread`); }}
                        className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#C9A24B] text-[#0A0A0A] shadow-[0_0_18px_rgba(201,162,75,.55)] wt-pulse hover:brightness-110">
                        <Inbox className="w-3 h-3" /> {unread} New Lead{unread > 1 ? "s" : ""}
                      </button>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-bold text-lg truncate">{s.business_name}</h3>
                    <p className="text-xs text-zinc-500 truncate mb-3">{s.subdomain}.webtu.in</p>

                    <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                      <Stat icon={Eye} value={s.stats?.views} label="Views" />
                      <Stat icon={MessageCircle} value={s.stats?.whatsapp_clicks} label="Chats" />
                      <Stat icon={Phone} value={s.stats?.calls} label="Calls" />
                    </div>

                    <div className="flex gap-2">
                      <button data-testid={`dashboard-quick-edit-btn-${i}`} onClick={() => navigate(`/sites/${s.id}`)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm hover:border-[#C9A24B]/50 transition-all">
                        <PencilLine className="w-4 h-4" /> Manage
                      </button>
                      <button onClick={() => navigate(`/preview/${s.id}`)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#C9A24B]/10 border border-[#C9A24B]/30 text-[#E5C378] text-sm transition-all">
                        <Eye className="w-4 h-4" /> Preview
                      </button>
                      <a href={siteHtmlUrl(s.id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center justify-center px-3 py-2 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-zinc-300 hover:border-[#C9A24B]/50 transition-all">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <a data-testid={`dashboard-share-wa-${i}`}
                        href={`https://wa.me/?text=${encodeURIComponent(`Check out ${s.business_name}'s website: ${siteHtmlUrl(s.id)}`)}`}
                        target="_blank" rel="noreferrer" title="Share on WhatsApp"
                        className="inline-flex items-center justify-center px-3 py-2 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/20 transition-all">
                        <MessageCircle className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-[#0A0A0A] rounded-xl py-2 border border-[#27272A]">
      <Icon className="w-4 h-4 text-[#C9A24B] mx-auto mb-1" />
      <div className="text-sm font-bold">{value ?? 0}</div>
      <div className="text-[10px] text-zinc-500">{label}</div>
    </div>
  );
}
